import React from 'react';
import _isNil from 'lodash/isNil';
import { useIntl, defineMessages } from 'react-intl';
import { makeStyles, Theme } from '@lifeomic/chroma-react/styles';
import SurveyResponseResults, {
  QuestionResponseResult,
  ResponseResult,
  SectionResponseResult,
} from '../../../Surveys/util/SurveyResponseResults';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { Text } from '@lifeomic/chroma-react/components/Text';
import ScoringContainer from './ScoringContainer';
import ResponseLineItem from './ResponseLineItem';
import InLineResponseLineItem from './InLineQuestionLineItem';
import { Divider } from '@lifeomic/chroma-react/components/Divider';

const messages = defineMessages({
  sum: {
    id: 'SectionLineItem.sum',
    defaultMessage: 'Sum: {value}',
  },
  avg: {
    id: 'SectionLineItem.avg',
    defaultMessage: 'Average: {value}',
  },
});

export const useStyles = makeStyles((theme: Theme) => ({
  labelContainer: {},
  label: {
    fontSize: theme.pxToRem(18),
    maxWidth: '70ch',
    margin: theme.spacing(1.5, 0, 1.5, 4),
  },
  childContainer: {
    '& > hr + div > $labelContainer': {
      '& > $label': {
        color: theme.palette.text.secondary,
        fontSize: theme.pxToRem(14),
      },
    },
    '& $childContainer': {
      margin: theme.spacing(0.5, 0, 0),
      '& p': {
        fontWeight: 'normal',
        '&:first-child': {
          marginLeft: theme.spacing(8),
        },
      },
      '& > *:not(hr)': {
        margin: 0,
      },
    },
  },
  scoreBox: {
    [theme.breakpoints.up('xl')]: {
      maxWidth: theme.pxToRem(1200),
    },
  },
  divider: {
    margin: 0,
  },
}));

export type SectionLineItemOwnProps = {
  item: SectionResponseResult;
  parentHasScoring: boolean;
  selectedResponse: fhir.QuestionnaireResponse;
  onFavoriteUpdate?: (resource: fhir.QuestionnaireResponse) => void;
};

export type SectionLineItemProps = SectionLineItemOwnProps;

type SingleSubResult = {
  type: 'single';
  item: ResponseResult;
};

type GroupedSubResult = {
  type: 'grouped';
  items: QuestionResponseResult[];
};

type SubResult = GroupedSubResult | SingleSubResult;

const SectionLineItem: React.FC<SectionLineItemProps> = ({
  item,
  parentHasScoring,
  selectedResponse,
  onFavoriteUpdate,
}) => {
  const { formatMessage } = useIntl();
  const classes = useStyles({});
  const sectionHasScoring = !_isNil(item.sum) || !_isNil(item.avg);
  const childParentHasScoring = sectionHasScoring || parentHasScoring;

  const { subResults } = item;
  const groupedSubResults = React.useMemo(
    () =>
      subResults.reduce((lines: SubResult[], item: ResponseResult) => {
        if (!lines.length) {
          return [
            {
              type: 'single',
              item,
            } as SingleSubResult,
          ];
        } else if (
          SurveyResponseResults.isSectionResult(item) ||
          !item.sameLine
        ) {
          return [
            ...lines,
            {
              type: 'single',
              item,
            } as SingleSubResult,
          ];
        }
        const copied = [...lines];
        const lastLine = copied.pop();
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        if (lastLine.type === 'grouped') {
          return [
            ...copied,
            {
              type: 'grouped',
              // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
              items: [...lastLine.items, item],
            } as GroupedSubResult,
          ];
        }
        return [
          ...copied,
          {
            type: 'grouped',
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            items: [lastLine.item, item],
          } as GroupedSubResult,
        ];
      }, [] as SubResult[]),
    [subResults],
  );

  const renderScoreResult = (contents: string) => (
    <>
      <Divider className={classes.divider} />
      <Box className={classes.scoreBox} fullWidth justify="flex-end">
        <ScoringContainer>
          <Text size="caption" weight="bold">
            {contents}
          </Text>
        </ScoringContainer>
      </Box>
    </>
  );

  return (
    <>
      <Divider className={classes.divider} />
      <Box direction="column" fullWidth>
        <Box className={classes.labelContainer} align="center">
          <Text size="subbody" weight="bold" className={classes.label}>
            {item.label}
          </Text>
        </Box>
        <Box className={classes.childContainer} direction="column" fullWidth>
          {groupedSubResults.map((subResult, index) =>
            subResult.type === 'grouped' ? (
              <InLineResponseLineItem
                key={index}
                lineItems={subResult.items}
                parentHasScoring={childParentHasScoring}
              />
            ) : (
              <ResponseLineItem
                key={index}
                lineItem={subResult.item}
                parentHasScoring={childParentHasScoring}
                selectedResponse={selectedResponse}
                onFavoriteUpdate={onFavoriteUpdate}
              />
            ),
          )}
        </Box>
        {!_isNil(item.sum) &&
          renderScoreResult(formatMessage(messages.sum, { value: item.sum }))}
        {!_isNil(item.avg) &&
          renderScoreResult(formatMessage(messages.avg, { value: item.avg }))}
      </Box>
    </>
  );
};

export default React.memo(SectionLineItem);
